/* eslint-disable global-require */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Img from 'gatsby-image';
import { withPrefix } from 'gatsby-link';
import { css, cx } from 'emotion';
import Color from 'color';
import shortid from 'shortid';
import smartypants from 'smartypants';

import 'flickity/dist/flickity.css';

import gray from '../utils/gray';
import { navBreakpoint } from '../utils/config';
import presets, { colors, baselineGrid, zIndex } from '../utils/presets';
import { options, rhythm } from '../utils/typography';
import { toRem, toVW, mqObj as mq } from '../utils/functions';

import InnerWrapper from '../components/inner-wrapper';
import Button from '../components/button';
import EyeIcon from '../assets/svg-sprite/shape-eye.svg';

const { curve, speed } = presets.animation;

const browserChromeDotColor = colors.gray.calm;

const browserChromeDotColors = [
  Color(browserChromeDotColor)
    .hsl()
    .toString(),
  Color(browserChromeDotColor)
    .hsl()
    .alpha(0.76)
    .toString(),
  Color(browserChromeDotColor)
    .hsl()
    .alpha(0.52)
    .toString(),
];

const cover = {
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
};

const heroBackgroundColor = Color(colors.ui.darkPurple)
  .hsl()
  .alpha(0.8)
  .string();

const themeHero = css`
  position: relative;
  margin-top: ${toRem(baselineGrid * -15)};
  padding-top: ${toRem(baselineGrid * 15)};

  @media (min-width: ${navBreakpoint}px) {
    margin-top: ${toRem(baselineGrid * -20)};
    padding-top: ${toRem(baselineGrid * 20)};
  }
`;

const themeHeroBackground = css`
  ${cover};
  background-color: ${colors.gray.dark};
  overflow: hidden;
  z-index: -1;

  &::before {
    ${cover} content: '';
    display: block;
    background-color: ${heroBackgroundColor};
    z-index: ${zIndex[2]};
  }
`;

const themeHeroBackgroundImg = css`
  width: 100%;
  height: 100%;
  margin: 0;
  object-fit: cover;
  object-position: center;
  z-index: ${zIndex[1]};
  /* filter: url('#hero-blur'); */
  filter: blur(calc(${toRem(baselineGrid)} + 1vw));
  transform: scale(1.2);
`;

const themeHeroWrapper = css`
  overflow-x: hidden;

  @media (min-width: ${navBreakpoint}px) {
    margin-top: ${toRem(baselineGrid * 4)};
  }

  ${mq.lapLarge} {
    display: flex;
    flex-wrap: nowrap;
    justify-content: space-between;
    align-content: flex-end;
  }

  ${mq.desk} {
    margin-top: ${toRem(baselineGrid * 6)};
  }
`;

const themeHeader = css`
  text-align: center;
  color: ${colors.gray.dark};

  .dark-header & {
    color: ${colors.ui.bright};
  }

  ${mq.lapLarge} {
    display: flex;
    flex-flow: column;
    justify-content: flex-start;
    flex: 0 0 auto;
    width: 40%;
    padding-right: ${toVW(baselineGrid * 4)};
    text-align: left;
  }

  ${mq.desk} {
    margin-top: ${toRem(baselineGrid * 2)};
    padding-right: ${toVW(baselineGrid * 5)};
  }

  ${mq.deskLarge} {
    margin-top: ${toRem(baselineGrid * 6)};
  }
`;

const themeName = css`
  margin: ${toRem(baselineGrid * 2)} 0;
  font-size: ${toRem(baselineGrid * 3)};
  letter-spacing: ${1 / 24}em;
  color: ${colors.gray.dark};

  .dark-header & {
    color: ${colors.ui.bright};
  }

  ${mq.lap} {
    font-size: ${toRem(baselineGrid * 4)};
  }

  ${mq.desk} {
    font-size: ${toRem(baselineGrid * 5)};
  }
`;

const themeDescription = css`
  font-size: 16px;
  line-height: 1.5;
  padding: 0 ${toRem(baselineGrid * 2)};
  color: ${gray(13, 0, false)};

  .dark-header & {
    color: ${gray(64, 0, true)};
  }

  ${mq.lapLarge} {
    padding: 0 ${toRem(baselineGrid * 4)} 0 0;
  }
`;

const themeHeroActions = css`
  margin-bottom: ${rhythm(options.blockMarginBottom)};
`;

const themeShowcase = css`
  align-self: flex-end;
  margin: 0 ${toRem(presets.baselineGrid * -2)};

  ${mq.palm} {
    margin: 0 ${toRem(presets.baselineGrid * -3)};
  }

  ${mq.lap} {
    margin: 0 ${toRem(presets.baselineGrid * -4)};
  }

  ${mq.lapLarge} {
    width: 100%;
    flex: 0 1 auto;
    margin: 0;
  }
`;

const screenshotWrapper = css`
  width: 90vw;
  padding: 0 3vw;
  cursor: url(${withPrefix('cursors/cursor-chevron-fat.svg')}) 9 14,
    url(${withPrefix('cursors/cursor-chevron-fat.png')}) 9 14, pointer;

  &:first-child {
    padding: 0 0 0 6vw;
  }

  &:last-child {
    padding: 0 6vw 0 0;
  }

  ${mq.lap} {
    width: 70vw;
  }

  ${mq.lapLarge} {
    width: 100%;

    &,
    &:first-child,
    &:last-child {
      padding: 0 2vw;
    }
  }
`;

const screenshot = css`
  position: relative;
`;

const screenshotLarge = css`
  position: relative;
  padding-top: ${toRem(baselineGrid * 1.5)};
  border-radius: 3px 3px 0 0;
  box-shadow: 0 2px 5px ${gray(80)};
  opacity: 0.72;
  transition: opacity ${speed.default} ${curve.default}, transform ${speed.fast} ${curve.default};

  .carousel-cell.is-selected & {
    opacity: 1;
  }

  /* browser chrome */
  &::before {
    content: '';
    display: block;
    position: absolute;
    height: ${toRem(baselineGrid * 1.5)};
    top: 0;
    right: 0;
    left: 0;
    background-color: ${gray(70, 'cool')};
    border-radius: 3px 3px 0 0;
  }

  &::after {
    content: '';
    display: block;
    position: absolute;
    width: 4px;
    height: 4px;
    top: ${toRem(baselineGrid * 0.75)};
    left: ${toRem(baselineGrid * 0.75)};
    background-color: ${browserChromeDotColors[0]};
    transform: translate(0, -50%);
    border-radius: 50%;
    box-shadow: 7px 0 0 0 ${browserChromeDotColors[1]}, 14px 0 0 0 ${browserChromeDotColors[2]};
  }

  ${mq.desk} {
    padding-top: ${toRem(baselineGrid * 2)};
    border-radius: 5px 5px 0 0;

    &::before {
      height: ${toRem(baselineGrid * 2)};
      border-radius: 5px 5px 0 0;
    }

    &::after {
      width: 6px;
      height: 6px;
      top: ${toRem(baselineGrid)};
      left: ${toRem(baselineGrid)};
      box-shadow: 10px 0 0 0 ${browserChromeDotColors[1]}, 20px 0 0 0 ${browserChromeDotColors[2]};
    }
  }
`;

const screenshotLargeWrapper = css`
  position: relative;
  padding-bottom: ${800 / 1280 * 100}%;
  overflow: hidden;
`;

const screenshotMobile = css`
  position: absolute;
  width: 22%;
  max-height: 52%;
  bottom: 0;
  right: calc(-3vw + 1px);
  padding: 4vw 2px 0;
  border-radius: 1.8vw 1.8vw 0 0;
  background-color: ${colors.gray.dark};
  z-index: ${zIndex[1]};
  overflow: hidden;
  visibility: hidden;
  opacity: 0;
  transform: translate3d(0, 16%, 0);
  transition: opacity ${speed.default} ${curve.default}, transform ${speed.default} ${curve.default};
  transition-delay: ${speed.fast};

  &::after {
    content: '';
    display: block;
    position: absolute;
    width: 20%;
    height: 2px;
    top: 2vw;
    left: 50%;
    transform: translate3d(-50%, -50%, 0);
    border-radius: 1vw;
    background-color: ${gray(32, 'cool', true)};
  }

  .carousel-cell.is-selected & {
    visibility: visible;
    opacity: 1;
    transform: translate3d(0, 1%, 0);
  }

  ${mq.lap} {
    padding: 3.2vw 2px 0;
    border-radius: 1.2vw 1.2vw 0 0;

    &::after {
      top: 1.6vw;
    }
  }

  ${mq.lapLarge} {
    right: calc(-2vw + 1px);
    padding: 2.6vw 3px 0;
    border-radius: 1vw 1vw 0 0;

    &::after {
      top: 1.3vw;
    }
  }

  ${mq.deskLarge} {
    right: calc(-2vw + 1px);
    padding: 2.2vw 4px 0;
    border-radius: 1vw 1vw 0 0;

    &::after {
      height: 3px;
      top: 1.1vw;
    }
  }
`;

const screenshotFrame = css({
  ...cover,
});

const themeHeroButton = css({
  marginRight: toRem(baselineGrid * 2),
  marginBottom: toRem(baselineGrid * 2),
});

const flickityOptions = {
  draggable: true,
  cellAlign: 'center',
  cellSelector: '.carousel-cell',
  prevNextButtons: false,
  pageDots: false,
  contain: true,
  selectedAttraction: 0.08,
  friction: 0.56,
};

class ThemeHero extends Component {
  constructor(props) {
    super(props);

    this.state = {
      Flickity: null,
    };

    if (typeof window !== 'undefined') {
      const Flickity = require('react-flickity-component');
      this.state.Flickity = Flickity;
    }
  }

  componentDidMount() {
    this.flkty.on('staticClick', (event, pointer, cellElement, cellIndex) => {
      if (cellElement && cellIndex < this.flkty.cells.length) {
        this.flkty.next(true);
      }
    });
  }

  componentWillUnmount() {
    this.flkty.off('staticClick');
  }

  render() {
    const { Flickity } = this.state;
    const {
      name,
      excerpt,
      price,
      demos,
      purchaseURL,
      backgroundColor,
      backgroundImage,
      images,
    } = this.props;

    const coloredBackground =
      backgroundColor &&
      css`
        &::before {
          background-color: ${backgroundColor};
        }
      `;

    return (
      <section className={themeHero}>
        <div className={cx(themeHeroBackground, coloredBackground)}>
          {backgroundImage && (
            <img
              src={backgroundImage.childImageSharp.sizes.src}
              className={themeHeroBackgroundImg}
            />
          )}
          <div
            className={css`
              position: absolute;
              top: 0;
              left: -9999em;
            `}
          >
            <svg>
              <defs>
                <filter id="hero-blur">
                  <feGaussianBlur in="SourceGraphic" stdDeviation="16" />
                  <feComponentTransfer>
                    <feFuncA type="discrete" tableValues="1 1" />
                  </feComponentTransfer>
                </filter>
              </defs>
            </svg>
          </div>
        </div>
        <InnerWrapper className={themeHeroWrapper}>
          <header className={themeHeader}>
            <h1 className={themeName}>{name}</h1>
            {excerpt && (
              <div
                className={themeDescription}
                dangerouslySetInnerHTML={{
                  __html: excerpt
                    .split('\n')
                    .map(p => `<p>${smartypants(p, 'qDew')}</p>`)
                    .join(''),
                }}
              />
            )}
            <div className={themeHeroActions}>
              <Button
                to={purchaseURL}
                className={themeHeroButton}
                shadow
                gradient={{
                  rotate: -16,
                  lighten: 0.08,
                }}
              >
                Get it for ${price}
              </Button>
              {demos.map((url, idx, arr) => {
                const label = arr.length === 1 ? 'Demo' : `Demo ${['One', 'Two', 'Three'][idx]}`;

                return (
                  <Button key={idx} to={url} className={themeHeroButton} hollow>
                    <svg viewBox={EyeIcon.viewBox}>
                      <use xlinkHref={`#${EyeIcon.id}`} />
                    </svg>
                    <span>{label}</span>
                  </Button>
                );
              })}
            </div>
          </header>
          <div className={themeShowcase}>
            {Flickity && (
              <Flickity
                className={'carousel'}
                options={flickityOptions}
                disableImagesLoaded={true}
                reloadOnUpdate={true}
                flickityRef={c => (this.flkty = c)} // eslint-disable-line no-return-assign
              >
                {images.map(image => (
                  <div key={shortid.generate()} className="carousel-cell" css={screenshotWrapper}>
                    <div className={screenshot}>
                      <div className={screenshotLarge}>
                        <div className={screenshotLargeWrapper}>
                          <div className={screenshotFrame}>
                            <Img alt="" sizes={image.desktop.childImageSharp.sizes} />
                          </div>
                        </div>
                      </div>
                      <div className={screenshotMobile}>
                        <Img alt="" sizes={image.mobile.childImageSharp.sizes} />
                      </div>
                    </div>
                  </div>
                ))}
              </Flickity>
            )}
          </div>
        </InnerWrapper>
      </section>
    );
  }
}

ThemeHero.propTypes = {
  name: PropTypes.string.isRequired,
  excerpt: PropTypes.string.isRequired,
  price: PropTypes.string.isRequired,
  demos: PropTypes.array.isRequired,
  purchaseURL: PropTypes.string.isRequired,
  backgroundColor: PropTypes.string,
  backgroundImage: PropTypes.object,
  images: PropTypes.array.isRequired,
};

export default ThemeHero;

export const themeHeroFragment = graphql`
  fragment themeHero_details on MarkdownRemark {
    frontmatter {
      name
      excerpt
      price
      demos
      purchaseURL
      heroBackgroundColor
      heroBackgroundImage {
        childImageSharp {
          sizes {
            src
            base64
          }
        }
      }
      heroImages {
        desktop {
          childImageSharp {
            sizes(maxWidth: 1280) {
              ...GatsbyImageSharpSizes
            }
          }
        }
        mobile {
          childImageSharp {
            sizes(maxWidth: 500) {
              ...GatsbyImageSharpSizes
            }
          }
        }
      }
    }
  }
`;



// WEBPACK FOOTER //
// ./src/components/theme-hero.js